<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';

import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

/** ... */
export interface RouletteItem {
  id: string;
  title: string;
  image: string | IconDefinition;
}

export interface Props {
  items: RouletteItem[];
  direction?: 'up' | 'down';
}

const props = withDefaults(defineProps<Props>(), {
  direction: 'down',
});

const offset = ref(0);
const hasMouseCursor = ref(false);
const pausedAt = ref<number | null>(null);
const intervalId = ref<number | null>(null);

/** ... */
const computedItems = computed(() => {
  const items: RouletteItem[] = [];

  const [first, second, ...others] = props.items;

  if (!first) return items;

  if (!second) {
    items.push(first, first, first);
  } else if (!others.length) {
    items.push(first, second, first);
  } else {
    items.push(first, second, ...others);
  }

  return items.map((item, index) => ({
    ...item,
    id: `${item.id}-${index}`,
  }));
});

/** ... */
const len = computed(() => computedItems.value.length);

/** ... */
const displayedItems = computed(() => {
  if (!len.value) return [];

  // ...
  const center = computedItems.value[offset.value];

  let it = offset.value - 1;
  it = it < 0 ? len.value - 1 : it;
  // ...
  const top = computedItems.value[it];

  let ib = offset.value + 1;
  ib = ib > len.value - 1 ? 0 : ib;
  // ...
  const bottom = computedItems.value[ib];

  if (!top || !bottom || !center) {
    throw new Error('[...]');
  }

  return [top, center, bottom];
});

onMounted(() => {
  startCycle();
});

onUnmounted(() => {
  stopCycle();
});

/**
 * Cycle roulette up.
 */
function cycleUp() {
  offset.value = offset.value >= len.value - 1 ? 0 : offset.value + 1;
}

/**
 * Cycle roulette down.
 */
function cycleDown() {
  offset.value = offset.value <= 0 ? len.value - 1 : offset.value - 1;
}

function runCycle() {
  if (hasMouseCursor.value) return;

  if (pausedAt.value) {
    const elapsedTime = Date.now() - pausedAt.value;

    if (elapsedTime < 10_000) return;

    pausedAt.value = null;
  }

  if (props.direction === 'up') {
    cycleUp();
  } else {
    cycleDown();
  }
}

/**
 * Start roulette cycle animation.
 */
function startCycle() {
  if (intervalId.value) return;

  intervalId.value = window.setInterval(runCycle, 5_000);
}

/**
 * Stop roulette cycle animation.
 */
function stopCycle() {
  if (!intervalId.value) return;

  window.clearInterval(intervalId.value);
}

/**
 * Pause roulette cycle animation.
 */
function pauseCycle() {
  pausedAt.value = Date.now();
}

/**
 * Set the current focus by an item index number.
 */
function setToFocus(index: number) {
  const direction = index === 0 ? 'up' : index === 2 ? 'down' : null;

  if (!direction) return;

  pauseCycle();

  if (direction === 'down') {
    cycleUp();
  } else {
    cycleDown();
  }
}
</script>

<template>
  <div
    :class="['feature-roulette', `direction-${direction}`]"
    @mouseenter="hasMouseCursor = true"
    @mouseleave="hasMouseCursor = false"
  >
    <TransitionGroup tag="div" class="roulette-wheel">
      <div
        v-for="(item, index) in displayedItems"
        :key="item.id"
        :class="[
          'roulette-item',
          {
            'is-top': index === 0,
            'is-focus': index === 1,
            'is-bottom': index === 2,
          },
        ]"
        @click="setToFocus(index)"
      >
        <div>
          <div v-if="typeof item.image === 'object'" class="item-icon">
            <Icon :icon="item.image" />
          </div>

          <div
            v-else
            class="item-image"
            :style="{ backgroundImage: `url(${item.image})` }"
          ></div>

          <div class="item-title">{{ item.title }}</div>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped lang="scss">
.feature-roulette {
  min-width: 200px;
  cursor: default;
}

.roulette-wheel {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
}

.roulette-item {
  transform: translateY(0);
  transition: transform 0.5s, opacity 0.5s, padding 0.5s, font-size 0.5s;
  padding: 0 20%;
  opacity: 0.5;
  position: absolute;
  width: 100%;
  height: calc((1 / 3) * 100%);
  user-select: none;

  &.is-focus {
    padding: 0 5%;
    opacity: 1;
    transform: translateY(0);
    pointer-events: none;
  }

  &.is-top {
    transform: translateY(-100%);
    font-size: 0.8em;

    @include v-enter {
      transform: translateY(-200%) !important;
    }

    @include v-leave-active {
      transform: translateY(-200%) !important;
    }
  }

  &.is-bottom {
    transform: translateY(100%);
    font-size: 0.8em;

    @include v-enter {
      transform: translateY(200%) !important;
    }

    @include v-leave-active {
      transform: translateY(200%) !important;
    }
  }

  > div {
    display: inline-flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    transform: scale(1);
    transition: transform 0.25s;

    &:hover {
      transform: scale(1.1);
    }
  }
}

.item-image {
  width: 100%;
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;
  flex-grow: 1;
}

.item-icon {
  flex-grow: 1;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  .fa-icon {
    color: white;
    width: 100%;
    font-size: 4rem;
  }
}

.item-title {
  width: 100%;
  color: white;
  padding: 10px 0;
  transform: translateY(0);
  transition: transform 0.25s;

  .roulette-item:not(.is-focus) & {
    transform: translateY(-40px);
  }
}
</style>
